import React, {useEffect, useMemo, useState} from "react";
import {TitleLayout} from "~/apps/core/ui/TitleLayout";
import {useAutoVisit, useCancelAutoVisit} from "~/apps/packages/auto-visits/hooks";
import {useHistory, useParams} from "react-router-dom";
import {CircularProgress, Box, Button, Snackbar, Typography, AlertColor} from "@mui/material";
import {AutoVisitDetail} from "~/apps/packages/auto-visits/components/AutoVisitDetail/AutoVisitDetail";
import {canEditAutoVisit} from "~/apps/packages/auto-visits/utils";
import { Alert } from '@mui/material';
import { AlertProps } from '@mui/lab';
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {useErrorHandler} from "~/components/ErrorHandling";
import {LoadingBox} from "~/apps/core/ui/LoadingBox";
import {ConfirmActionDialog} from "~/components/ConfirmActionDialog";
import {PopupMessage} from "~/components/PopupMessage";
import {ErrorMessage} from "~/components/ErrorHandling/AlertErrorHandlerProvider";

type NotificationType = ErrorMessage & { severity: AlertColor };

const successNotification: NotificationType = {
    title: 'Автовизит отменен',
    message: 'Статус автовизита изменен',
    severity: 'success'
}

let unSuccessNotification: NotificationType = {
    title: 'Ошибка',
    message: 'Не удалось отменить автовизит',
    severity: 'error'
}

export const DetailPage = () => {
    const history = useHistory();
    const {id} = useParams<{ id: string }>();
    const {data, loading} = useAutoVisit(id);
    const [cancel, {
        loading: canceling,
        error: cancelError,
        success: cancelled
    }] = useCancelAutoVisit(id);
    const [alert, setAlert] = useState<{open: boolean, notification: NotificationType}>({open: false, notification: null});
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const canEdit = useMemo(() => {
        return data && canEditAutoVisit(data);
    }, [data, canceling]);

    const disabledEdit = useMemo(() => {
        return canceling;
    }, [canceling]);

    useEffect(() => {
        if (cancelError) {
            let notification = unSuccessNotification;
            if (cancelError?.message) notification.message = cancelError.message
            setAlert({
                open: true,
                notification: notification
            });
        }
    }, [cancelError]);

    useEffect(() => {
        if (cancelled) {
            setAlert({
                open: true,
                notification: successNotification
            });
        }
    }, [cancelled])

    const handleEditClick = () => {
        history.push(`/${id}/edit`);
    }

    const handleCancelClick = () => {
        setIsCancelDialogOpen(true);
    }

    const handleConfirmCancel = () => {
        cancel();
        setIsCancelDialogOpen(false)
    }

    const handleAlertClose = () => {
        setAlert({
            open: false,
            notification: null
        });
    }

    return (
        <TitleLayout title="Автовизит">
            <ConfirmActionDialog
                open={isCancelDialogOpen}
                dialogId={"removeCargo"}
                confirmButtonText="Подтвердить"
                onClose={() => setIsCancelDialogOpen(null)}
                onConfirm={handleConfirmCancel}
            >
                <Typography width="100%" align="center" variant="screenText">
                    Отменить автовизит?
                </Typography>
            </ConfirmActionDialog>
            <LoadingBox loading={loading}>
                {data && (
                    <Box pt={2}>
                        <AutoVisitDetail autoVisit={data}/>
                    </Box>
                )}
            </LoadingBox>

            {canEdit && (
                <>
                    <Box height={136} minHeight={136}/>
                    <FloatingActions>
                        <Button
                            fullWidth
                            color="primary"
                            onClick={handleEditClick}
                            disabled={disabledEdit}
                        >
                            Изменить
                        </Button>

                        <Button
                            fullWidth
                            color="secondary"
                            startIcon={canceling && <CircularProgress size={16} color="inherit"/>}
                            disabled={disabledEdit || canceling}
                            onClick={handleCancelClick}
                        >
                            {canceling ? "Отмена" : "Отменить"}
                        </Button>
                    </FloatingActions>
                </>
            )}

            <PopupMessage
                onClose={handleAlertClose}
                open={alert.open}
                notification={alert.notification}
                autoHideDuration={5000}
                severity={alert.notification?.severity}
            />
        </TitleLayout>
    )
}
